// "Waiting for you" on Just Play (Paul, 19 Oct 2026: "within Just play there should be a list of pending
// invites / sessions"). Signed-in people only, and only when something is pending:
//   - Partnership bidding sets where it's your turn, and sets waiting on your partner (partnerBidding "list")
//   - Weekly tournament and partner invites sent to you (partnerHub "inbox"; the pop-up answers them)
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { firebase } from "../../firebase/config";
import { pb, testUid } from "../PartnerBidding/pbApi";
import { ph } from "../PartnerHub/phApi";
import { sourceLabel } from "../PartnerBidding/pbLabels";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The x on a row (Paul, 31 Oct 2026: "User needs to be able to get rid of this ... Should still be available in
// partnership bidding area. Or give confirmation to delete versus hide") opens a choice:
//   Hide here  - stays hidden on this device for this account while the wording is unchanged; if the item changes
//                (more boards for you, a new invite) it comes back by itself. Nothing is written to the server and
//                the set is still listed under Partnership bidding.
//   Remove set - the server "leave" action, the same as "Remove this set from my list" on the set page; the
//                partner's copy is untouched. Invites have no remove here (they are answered on /partner).
const hiddenKey = (uid) => `jpw-hidden-${uid}`;
function readHidden(uid) {
  try { return JSON.parse(localStorage.getItem(hiddenKey(uid)) || "{}") || {}; } catch (e) { return {}; }
}
function writeHidden(uid, h) {
  try { localStorage.setItem(hiddenKey(uid), JSON.stringify(h)); } catch (e) { /* private mode: hide for this page view only */ }
}

export default function JustPlayWaiting() {
  const [uid, setUid] = useState(null);
  const [items, setItems] = useState(null);
  const [hidden, setHidden] = useState({});
  const [asking, setAsking] = useState(null); // key of the row whose hide/remove choice is open
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = testUid(); // localhost test players only (?as=...); never on the live site
    if (t) {
      setUid(t);
      return undefined;
    }
    return firebase.auth().onAuthStateChanged((u) => setUid(u ? u.uid : null));
  }, []);

  useEffect(() => { setHidden(uid ? readHidden(uid) : {}); }, [uid]);

  useEffect(() => {
    if (!uid) {
      setItems(null);
      return undefined;
    }
    let live = true;
    const load = async () => {
      const [sets, inbox] = await Promise.all([pb("list").catch(() => null), ph("inbox").catch(() => null)]);
      const out = [];
      ((inbox && inbox.incoming) || []).forEach((x) => {
        const play = x.kind === "play" || (!x.kind && x.pairId);
        const event = x.kind === "event" && x.eventId;
        out.push({
          key: `inv-${x.id}-${x.at || 0}`,
          to: event ? `/events/${x.eventId}` : play && x.pairId ? `/partner/${x.pairId}/weekly` : "/partner",
          text: event ? `${x.fromName || "Your partner"} entered you both in ${x.eventName || "an event"}` : play ? `${x.fromName || "Your partner"} invited you to the weekly tournament` : `${x.fromName || "Someone"} invited you to be partners`,
          you: true,
        });
      });
      ((sets && sets.sets) || []).forEach((s) => {
        const who = s.partnerName || "your partner";
        const kind = sourceLabel(s.source);
        if (s.yourTurn > 0) {
          out.push({ key: `pb-${s.id}`, setId: s.id, to: `/partner-bidding/${s.id}`, text: `Partnership bidding with ${who} · ${kind}: your turn on ${plural(s.yourTurn, "board", "boards")}`, you: true });
        } else if (s.partnerTurn > 0) {
          out.push({ key: `pb-${s.id}`, setId: s.id, to: `/partner-bidding/${s.id}`, text: `Partnership bidding with ${who} · ${kind}: waiting for ${who} (${plural(s.partnerTurn, "board", "boards")})`, you: false });
        }
      });
      if (live) setItems(out);
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => { live = false; clearInterval(t); };
  }, [uid]);

  const hide = (x) => {
    const h = { ...hidden, [x.key]: x.text };
    setHidden(h);
    writeHidden(uid, h);
    setAsking(null);
  };

  const remove = async (x) => {
    setBusy(true);
    try {
      await pb("leave", { setId: x.setId });
      setItems((cur) => (cur || []).filter((i) => i.key !== x.key));
      setAsking(null);
    } catch (e) {
      // leave failed (offline?): fall back to hiding it here so the tap still does something
      hide(x);
    } finally {
      setBusy(false);
    }
  };

  const shown = (items || []).filter((x) => hidden[x.key] !== x.text);
  if (shown.length === 0) return null;
  const sorted = [...shown].sort((a, b) => Number(b.you) - Number(a.you));
  return (
    <section className="jpw" aria-labelledby="jpw-title">
      <h2 id="jpw-title" className="jpw-title">Waiting for you</h2>
      <ul className="jpw-list">
        {sorted.map((x) => (
          <li key={x.key} className="jpw-item">
            {asking === x.key ? (
              <div className="jpw-ask" role="group" aria-label="Hide or remove">
                <p className="jpw-ask-text">{x.text}</p>
                <div className="jpw-ask-btns">
                  <button type="button" className="jpw-btn" onClick={() => hide(x)} disabled={busy}>
                    Hide here only
                  </button>
                  {x.setId && (
                    <button type="button" className="jpw-btn jpw-btn--danger" onClick={() => remove(x)} disabled={busy}>
                      {busy ? "Removing…" : "Remove set from my list"}
                    </button>
                  )}
                  <button type="button" className="jpw-btn jpw-btn--plain" onClick={() => setAsking(null)} disabled={busy}>
                    Cancel
                  </button>
                </div>
                <p className="jpw-ask-note">
                  {x.setId
                    ? "Hide keeps the set under Partnership bidding. Remove takes it off your list; your partner keeps theirs."
                    : "Hide keeps the invite under With Partner."}
                </p>
              </div>
            ) : (
              <>
                <Link to={x.to} className={`jpw-row ${x.you ? "jpw-row--you" : ""}`}>
                  <span className="jpw-dot" aria-hidden="true" />
                  <span className="jpw-text">{x.text}</span>
                  <span className="jpw-arrow" aria-hidden="true">→</span>
                </Link>
                <button type="button" className="jpw-hide" onClick={() => setAsking(x.key)} aria-label="Hide or remove" title="Hide or remove">
                  ×
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
